import { useApiClient } from '../../hooks/useApiClient'
import { useApi } from '@gravitate-js/the-armory'
import { getAPIURL } from '../../utils/api'
import { useQuery } from '@tanstack/react-query'
import type { RecentActivityAPIResponse, RecentActivityRequest } from './types/recentActivity'

// Date range interface for cleaner API
interface DateRange {
  start: Date
  end: Date
}

/**
 * Hook for fetching recent activity data from the supplier reconciliation API
 * Following the established pattern from useMatches.ts
 */
export const useRecentActivity = () => {
  const $api = useApiClient()
  const api = useApi({ baseURLOverride: getAPIURL() })

  /**
   * Fetch recent activity timeline with optional filtering
   */
  const useRecentActivityQuery = (params: {
    dateRange?: DateRange
    assignedToMe?: boolean
    limit?: number
  }) => {
    const { dateRange, assignedToMe, limit = 20 } = params

    return useQuery({
      queryKey: ['supplier_rec_recent_activity', dateRange, assignedToMe, limit],
      queryFn: async (): Promise<RecentActivityAPIResponse> => {
        const requestBody: RecentActivityRequest = {
          limit,
        }

        // Add date range if provided
        if (dateRange) {
          requestBody.start_date = dateRange.start.toISOString()
          requestBody.end_date = dateRange.end.toISOString()
        }

        // Add assignedToMe filter if specified
        if (assignedToMe !== undefined) {
          requestBody.assigned_to_me = assignedToMe
        }

        const response = await api.post('supplier_rec/recent_activity', requestBody)
        return response as RecentActivityAPIResponse
      },
      refetchOnWindowFocus: false,
      // Temporarily disable auto-refetch for testing - will re-enable once working correctly
      // refetchInterval: 30000,
    })
  }

  return {
    useRecentActivity: useRecentActivityQuery,
  }
}
